
import React from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import LandingPage from '@/components/LandingPage';
import RedirectRoute from '@/components/RedirectRoute';
import RequireAuth from '@/components/RequireAuth';
import RecipeCollection from '@/pages/RecipeCollection';
import AddRecipes from '@/pages/AddRecipes';
import ImportRecipes from '@/pages/ImportRecipes';
import CreateQuickRecipe from '@/pages/CreateQuickRecipe';
import RecipePage from '@/pages/RecipePage';
import RecipeFinder from '@/pages/RecipeFinder';
import MealPlanner from '@/pages/MealPlanner';
import MealSelection from '@/pages/MealSelection';
import ShoppingList from '@/pages/ShoppingList';
import CalendarHistory from '@/pages/CalendarHistory';
import Compositions from '@/pages/Compositions';
import CreateComposition from '@/pages/CreateComposition';
import CompositionDetail from '@/pages/CompositionDetail';
import MealSuggestions from '@/pages/MealSuggestions';
import MonthlyMealView from '@/pages/MonthlyMealView';
import FamilyCircle from '@/pages/FamilyCircle';
import FamilyActivity from '@/pages/FamilyActivity';
import ProfilePage from '@/pages/ProfilePage';
import ShareFeedback from '@/pages/ShareFeedback';
import HowItWorks from '@/pages/HowItWorks';
import FAQ from '@/pages/FAQ';
import InstallApp from '@/pages/InstallApp';
import TermsOfService from '@/pages/TermsOfService';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import CookiePreferences from '@/pages/CookiePreferences';
import NotFound from '@/pages/NotFound';
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AboutPage from '@/pages/AboutPage';
import Tips from '@/pages/Tips';
import EditRecipePage from '@/pages/EditRecipePage';
import EditComposition from '@/pages/EditComposition';
import MealInspiration from '@/pages/MealInspiration';

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* SEO Redirects - Old URLs to new SEO-friendly URLs */}
      <Route path="/faq" element={<RedirectRoute to="/meal-planning-app-faqs" />} />
      <Route path="/how-it-works" element={<RedirectRoute to="/how-forkast-meal-planning-works" />} />
      <Route path="/ios-app" element={<RedirectRoute to="/install" />} />
      <Route path="/meal-planning-ios-app" element={<RedirectRoute to="/install" />} />
      <Route path="/tips" element={<RedirectRoute to="/meal-planning-tips" />} />

      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/meal-inspiration" element={<MealInspiration />} />
      <Route path="/how-forkast-meal-planning-works" element={<HowItWorks />} />
      <Route path="/meal-planning-tips" element={<Tips />} />
      <Route path="/meal-planning-app-faqs" element={<FAQ />} />
      <Route path="/install" element={<InstallApp />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/cookies" element={<CookiePreferences />} />

      {/* Auth routes */}
      <Route path="/auth" element={<Outlet />}>
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
      </Route>

      {/* Isolated Admin Dashboard - completely separate from core app */}
      <Route path="/admin-dashboard-xyz789" element={<AdminDashboard />} />

      {/* Protected routes */}
      <Route path="/recipes" element={<RequireAuth><RecipeCollection /></RequireAuth>} />
      <Route path="/recipe-collection" element={<RequireAuth><RecipeCollection /></RequireAuth>} />
      <Route path="/add-recipes" element={<RequireAuth><AddRecipes /></RequireAuth>} />
      <Route path="/import-recipes" element={<RequireAuth><ImportRecipes /></RequireAuth>} />
      <Route path="/create-recipe" element={<RequireAuth><CreateQuickRecipe /></RequireAuth>} />
      <Route path="/create-quick-recipe" element={<RequireAuth><CreateQuickRecipe /></RequireAuth>} />
      <Route path="/recipe/:id" element={<RequireAuth><RecipePage /></RequireAuth>} />
      <Route path="/edit-recipe/:id" element={<RequireAuth><EditRecipePage /></RequireAuth>} />
      <Route path="/recipe-finder" element={<RequireAuth><RecipeFinder /></RequireAuth>} />
      <Route path="/meal-planner" element={<RequireAuth><MealPlanner /></RequireAuth>} />
      <Route path="/meal-selection" element={<RequireAuth><MealSelection /></RequireAuth>} />
      <Route path="/meal-selection/:date" element={<RequireAuth><MealSelection /></RequireAuth>} />
      <Route path="/shopping-list" element={<RequireAuth><ShoppingList /></RequireAuth>} />
      <Route path="/wellness" element={<RequireAuth><CalendarHistory /></RequireAuth>} />
      <Route path="/meal-suggestions" element={<RequireAuth><MealSuggestions /></RequireAuth>} />
      <Route path="/monthly-meal-view" element={<RequireAuth><MonthlyMealView /></RequireAuth>} />
      <Route path="/compositions" element={<RequireAuth><Compositions /></RequireAuth>} />
      <Route path="/create-composition" element={<RequireAuth><CreateComposition /></RequireAuth>} />
      <Route path="/composition/:id" element={<RequireAuth><CompositionDetail /></RequireAuth>} />
      <Route path="/composition/:id/edit" element={<RequireAuth><EditComposition /></RequireAuth>} />
      <Route path="/family-circle" element={<RequireAuth><FamilyCircle /></RequireAuth>} />
      <Route path="/family-activity" element={<RequireAuth><FamilyActivity /></RequireAuth>} />
      <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
      <Route path="/about" element={<RequireAuth><AboutPage /></RequireAuth>} />
      <Route path="/feedback" element={<RequireAuth><ShareFeedback /></RequireAuth>} />
      <Route path="/share-feedback" element={<RequireAuth><ShareFeedback /></RequireAuth>} />

      {/* 404 route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
